// src/pages/Analytics.jsx
import React from "react";
import {
  Upload,
  PieChart,
  TrendingUp,
  LineChart,
  Trophy,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";

import { Line as CLine, Bar as CBar, Pie as CPie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import { getLenderDistribution } from "../../api/lenderPortal/lenderDistribution.js";
import {
  getIfTimeToCashPerStage,
  getIfTimeToCashPerStageRanges,
  pickIfTimeToCashRange,
  getTimeToCashPerStagePerLender,
  normalizeTimeToCashStages, // ✅ Option A normalizer (clamps negatives to 0)
} from "../../api/lenderPortal/timeToCash.js";
import { getAllPerformanceMetrics } from "../../api/lenderPortal/performanceMetrics.js";
import {
  getMonthlyTotalViews,
  getTimeframeFromRange,
  getTopProductMonthlyByTimeframe,
} from "../../api/lenderPortal/topProductsViewed.js";
import { getProductSummaryByType } from "../../api/lenderPortal/mortgageSummary.js";
import { api } from "../../api/client.js";

import Hero from "../../components/ui/Hero.jsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const EMPTY_DATASET = {
  kpi: { total: 0, active: 0, conversion: 0 },
  monthly: [],
  lenders: [],
};

const ACard = ({ className = "", children }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl ${className}`}>
    {children}
  </div>
);

/* ---------- KPI shimmer ---------- */
function useLiveDelta() {
  const [delta, setDelta] = React.useState(() =>
    Number((Math.random() * 2 - 1).toFixed(1))
  );
  React.useEffect(() => {
    const id = setInterval(() => {
      setDelta(Number((Math.random() * 6 - 3).toFixed(1)));
    }, 5000);
    return () => clearInterval(id);
  }, []);
  return delta;
}

const AKpi = ({ title, value, icon: Icon, loading }) => {
  const delta = useLiveDelta();
  const positive = delta >= 0;

  // Skeleton state while KPIs are loading (colors aligned with overview: sky palette)
  if (loading) {
    return (
      <ACard className="p-3 min-h-[84px]">
        <div className="flex items-center gap-3 animate-pulse">
          <div className="w-10 h-10 rounded-xl bg-sky-50 border border-sky-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-sky-100 rounded w-1/3" />
            <div className="h-4 bg-sky-200 rounded w-2/3" />
            <div className="h-3 bg-sky-100 rounded w-1/4" />
          </div>
        </div>
      </ACard>
    );
  }

  return (
    <ACard className="p-3 min-h-[84px] transition-transform transition-shadow duration-200 ease-out hover:-translate-y-1 hover:shadow-lg">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center bg-slate-100 border border-slate-200">
          {Icon ? <Icon className="w-4 h-4 text-slate-700" /> : null}
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 whitespace-nowrap">
            {title}
          </div>
          <div className="text-xl font-extrabold text-slate-900 tracking-tight truncate">
            {value}
          </div>
          <div
            className={`text-xs mt-0.5 ${positive ? "text-emerald-600" : "text-red-600"
              } flex items-center gap-1`}
            aria-live="polite"
          >
            <span>{positive ? "▲" : "▼"}</span>
            <span>{Math.abs(delta).toFixed(1)}%</span>
          </div>
        </div>
      </div>
    </ACard>
  );
};

/* ---------- Small hook: is auth session ready? ---------- */
function useAuthReady() {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    // Check for auth flag (set during login)
    const hasAuthFlag =
      localStorage.getItem("ds_isAuthed") === "1" ||
      sessionStorage.getItem("ds_isAuthed") === "1";

    // Check if cookies are present (cookie-based auth)
    const hasCookies = typeof document !== "undefined" && document.cookie.includes("sid");

    // If auth flag exists, mark as ready immediately
    // The API calls have their own retry logic for 401 errors
    if (hasAuthFlag) {
      // Give a small delay to ensure cookies are set after page load/redirect
      const timer = setTimeout(() => {
        setReady(true);
      }, 300);
      return () => clearTimeout(timer);
    }

    // If no auth flag but cookies exist (edge case), verify with backend
    if (hasCookies && !hasAuthFlag) {
      let cancelled = false;
      
      async function verifySession() {
        try {
          const response = await api.get("/api/method/frappe.auth.get_logged_user");
          if (!cancelled && response?.data) {
            setReady(true);
          }
        } catch (error) {
          // Session verification failed - don't mark as ready
          console.warn("Session verification failed in useAuthReady:", error);
        }
      }

      verifySession();
      return () => {
        cancelled = true;
      };
    }
  }, []);

  return ready;
}

/* ---------- Options ---------- */
const PRODUCT_OPTIONS = [
  { value: "btl", label: "Buy-to-Let Mortgage" },
  { value: "if", label: "Invoice Finance" },
];

const PRODUCT_DB = {
  btl: {
    kpi: { total: 324, active: 240, conversion: 5.6 },
    monthly: [10, 12, 9, 13, 12, 15, 16, 18, 17, 19, 20, 21],
    stages: [1, 5, 14, 8, 24, 3],
    lenders: [
      { lender: "Paragon", share: 420 },
      { lender: "Shawbrook", share: 370 },
      { lender: "Octane", share: 310 },
    ],
  },
  invoice: {
    kpi: { total: 156, active: 118, conversion: 4.2 },
    monthly: [8, 10, 7, 11, 9, 12, 14, 15, 14, 16, 17, 18],
    stages: [2, 4, 10, 6, 18, 2],
    lenders: [
      { lender: "Paragon", share: 280 },
      { lender: "Shawbrook", share: 250 },
    ],
  },
  if: {
    kpi: { total: 156, active: 118, conversion: 4.2 },
    monthly: [8, 10, 7, 11, 9, 12, 14, 15, 14, 16, 17, 18],
    stages: [2, 4, 10, 6, 18, 2],
    lenders: [
      { lender: "Paragon", share: 280 },
      { lender: "Shawbrook", share: 250 },
    ],
  },
  semi: {
    kpi: { total: 89, active: 67, conversion: 3.8 },
    monthly: [5, 7, 6, 8, 7, 9, 10, 11, 10, 11, 12, 13],
    stages: [1, 3, 8, 5, 15, 2],
    lenders: [
      { lender: "Paragon", share: 180 },
      { lender: "Shawbrook", share: 160 },
    ],
  },
  commercial: {
    kpi: { total: 67, active: 48, conversion: 3.2 },
    monthly: [4, 5, 4, 6, 5, 7, 8, 9, 8, 9, 10, 11],
    stages: [1, 3, 7, 4, 12, 2],
    lenders: [
      { lender: "Paragon", share: 150 },
      { lender: "Shawbrook", share: 130 },
    ],
  },
  complex: {
    kpi: { total: 45, active: 32, conversion: 2.8 },
    monthly: [3, 4, 3, 5, 4, 6, 7, 7, 8, 8, 9, 10],
    stages: [1, 2, 6, 4, 10, 1],
    lenders: [
      { lender: "Paragon", share: 120 },
      { lender: "Shawbrook", share: 100 },
    ],
  },
  bridging: {
    kpi: { total: 78, active: 54, conversion: 3.5 },
    monthly: [5, 6, 5, 7, 6, 8, 9, 10, 9, 10, 11, 12],
    stages: [1, 3, 8, 5, 14, 2],
    lenders: [
      { lender: "Paragon", share: 160 },
      { lender: "Shawbrook", share: 140 },
    ],
  },
};

const pieColors = [
  "#0EA5E9",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#14B8A6",
  "#A3E635",
  "#EC4899",
];

// Turn an email like "fleet@test.com" into a display name "Fleet"
const toLenderDisplayName = (label) => {
  const raw = String(label || "").trim();
  if (!raw || raw === "Others" || raw === "Unassigned") return raw;
  const local = raw.includes("@") ? raw.split("@")[0] : raw;
  const cleaned = local.replace(/[^a-zA-Z0-9]+/g, " ").trim();
  if (!cleaned) return raw;
  const first = cleaned.split(" ")[0];
  return first.charAt(0).toUpperCase() + first.slice(1);
};

// Compare two lender identifiers (email or name) in a tolerant way:
// - exact case-insensitive match, OR
// - matching email local parts (before '@')
const isSameLender = (a, b) => {
  const norm = (s) => String(s || "").trim().toLowerCase();
  const A = norm(a);
  const B = norm(b);
  if (!A || !B) return false;
  if (A === B) return true;
  const aLocal = A.split("@")[0];
  const bLocal = B.split("@")[0];
  return !!aLocal && aLocal === bLocal;
};
const formatGBP0 = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n ?? 0);


const toTopNPlusOthersRankWeighted = (rows, topN = 4, topShare = 0.92) => {
  const norm = (s) => String(s || "").trim().toLowerCase();
  const all = Array.isArray(rows) ? rows : [];
  const hasExplicitOthers = all.some((r) => norm(r.lender) === "others");

  // If backend already provides an explicit "Others" row, just take the
  // topN rows by share (including that "Others" row) and don't create a
  // synthetic Others bucket.
  if (hasExplicitOthers) {
    const sorted = [...all].sort(
      (a, b) => b.share - a.share || a.lender.localeCompare(b.lender)
    );
    const top = sorted.slice(0, Math.min(topN, sorted.length));

    const labels = top.map((r) => r.lender);
    const values = top.map((r) => r.share || 0);
    const detailsByLabel = {};

    top.forEach((r) => {
      detailsByLabel[r.lender] = {
        approvalRate: r.approvalRate,
        topPerformer: r.topPerformer,
        avgLoan: r.avgLoan,
        satisfaction: r.satisfaction,
      };
    });

    return { labels, values, detailsByLabel };
  }

  // Fallback behaviour when we DON'T have an explicit Others row:
  // build a synthetic Others bucket from the tail.
  const list = all;
  const sorted = [...list].sort(
    (a, b) => b.share - a.share || a.lender.localeCompare(b.lender)
  );
  const top = sorted.slice(0, Math.min(topN, sorted.length));
  const others = sorted.slice(top.length);
  const totalVol = list.reduce((s, r) => s + (r.share || 0), 0) || 1;

  const effectiveTopShare = others.length ? topShare : 1;
  const othersWeight = 1 - effectiveTopShare;

  const ranks = top.map((_, i) => top.length - i);
  const rankSum = ranks.reduce((a, b) => a + b, 0) || 1;
  const topWeights = ranks.map((w) => (effectiveTopShare * w) / rankSum);

  const values = [...topWeights.map((w) => w * totalVol)];
  const labels = [...top.map((r) => r.lender)];
  const detailsByLabel = {};

  top.forEach((r) => {
    detailsByLabel[r.lender] = {
      approvalRate: r.approvalRate,
      topPerformer: r.topPerformer,
      avgLoan: r.avgLoan,
      satisfaction: r.satisfaction,
    };
  });

  if (others.length) {
    const wSum = others.reduce((s, r) => s + (r.share || 0), 0) || 1;
    const w = (key) =>
      Math.round(
        others.reduce((s, r) => s + (r[key] || 0) * (r.share || 0), 0) / wSum
      );
    const wAvgLoan = Math.round(
      others.reduce((s, r) => s + (r.avgLoan || 0) * (r.share || 0), 0) / wSum
    );

    detailsByLabel["Others"] = {
      approvalRate: w("approvalRate"),
      topPerformer: w("topPerformer"),
      avgLoan: wAvgLoan,
      satisfaction: w("satisfaction"),
    };

    labels.push("Others");
    values.push(othersWeight * totalVol);
  }

  return { labels, values, detailsByLabel };
};

const RANGE_OPTIONS = ["Last 30 Days", "Last 3 Months", "Last 6 Months"];
const MONTH_LABELS_12 = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const monthsFromRange = (label) => {
  switch (label) {
    case "Last 3 Months":
      return 3;
    case "Last 6 Months":
      return 6;
    case "Last 30 Days":
    default:
      return 1;
  }
};

/** Slice monthly API data to match the selected range dropdown. */
const buildClicksChartForRange = (monthlyData, rangeLabel) => {
  const data = Array.isArray(monthlyData) ? monthlyData : [];
  const padded = MONTH_LABELS_12.map((_, idx) => Number(data[idx] ?? 0));
  const monthCount = monthsFromRange(rangeLabel);
  const currentMonth = new Date().getMonth();

  const labels = [];
  const values = [];

  for (let offset = monthCount - 1; offset >= 0; offset -= 1) {
    const idx = currentMonth - offset;
    if (idx >= 0) {
      labels.push(MONTH_LABELS_12[idx]);
      values.push(padded[idx]);
    }
  }

  if (!labels.length) {
    labels.push(MONTH_LABELS_12[currentMonth]);
    values.push(padded[currentMonth] ?? 0);
  }

  return { labels, values };
};

const RangeDropdown = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="text-xs border rounded px-2 py-1 bg-white"
  >
    {RANGE_OPTIONS.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

const PurposeDropdown = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="text-xs border rounded px-2 py-1 bg-white"
  >
    {["purchase", "remortgage", "product transfer"].map(
      (p) => (
        <option key={p} value={p}>
          {p.charAt(0).toUpperCase() + p.slice(1)}
        </option>
      )
    )}
  </select>
);

const getMyLenderRank = (lendersArr, myName) => {
  const norm = (s) => String(s || "").trim().toLowerCase();
  const list = Array.isArray(lendersArr) ? lendersArr : [];
  const sorted = list
    .slice()
    .sort(
      (a, b) =>
        Number(b.share || 0) - Number(a.share || 0) ||
        String(a.lender || "").localeCompare(String(b.lender || ""))
    );
  const idx = sorted.findIndex((r) => norm(r.lender) === norm(myName));
  return idx >= 0 ? idx + 1 : null;
};

/* ====== Time-to-Cash helpers ====== */
function normalizeTtcKeys(obj) {
  if (!obj || typeof obj !== "object") return {};
  const map = {};
  Object.keys(obj).forEach((k) => {
    map[k.trim().toLowerCase()] = obj[k];
  });
  return map;
}

function getStageBlock(block) {
  if (!block) return null;
  return block.avg_days_per_stage || block.stages || null;
}

// Treat "0 days" as valid data – we only care that a value exists
function hasAnyStageData(block) {
  const s = getStageBlock(block);
  if (!s) return false;

  const has = (key) => {
    const direct = s[key];
    const lower = s[key.toLowerCase()];
    return direct !== undefined || lower !== undefined;
  };

  return (
    has("Enquiry") ||
    has("Application") ||
    has("Approval") ||
    has("Valuation") ||
    has("Audit") ||
    has("Legals") ||
    has("Completion")
  );
}

function readLenderEmailFromSession() {
  if (typeof window === "undefined") return "";
  return (
    window.AUTH_LENDER_EMAIL ||
    sessionStorage.getItem("ds_lender_email") ||
    localStorage.getItem("ds_lender_email") ||
    sessionStorage.getItem("lender_email") ||
    localStorage.getItem("lender_email") ||
    sessionStorage.getItem("user_email") ||
    localStorage.getItem("user_email") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    sessionStorage.getItem("EXPECTED_SESSION_USER") ||
    localStorage.getItem("EXPECTED_SESSION_USER") ||
    ""
  );
}

export default function Analytics() {
  const [product, setProduct] = React.useState("btl");
  const [purpose, setPurpose] = React.useState("purchase");

  const [rangeLine, setRangeLine] = React.useState(RANGE_OPTIONS[0]);
  const [rangeBar, setRangeBar] = React.useState(RANGE_OPTIONS[0]); // Time-to-Cash range
  const [ttcMode, setTtcMode] = React.useState("count"); // "count" | "avgDays"
  const [rangePerf, setRangePerf] = React.useState(RANGE_OPTIONS[0]);
  const [rangePie, setRangePie] = React.useState(RANGE_OPTIONS[0]);
  const [isDarkTheme, setIsDarkTheme] = React.useState(() => {
    if (typeof document === "undefined") return false;
    const root = document.documentElement;
    const dataTheme = root.getAttribute("data-theme");
    if (dataTheme === "dark") return true;
    if (dataTheme === "light") return false;
    return root.classList.contains("dark");
  });

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const readTheme = () => {
      const dataTheme = root.getAttribute("data-theme");
      if (dataTheme === "dark") return true;
      if (dataTheme === "light") return false;
      return root.classList.contains("dark");
    };
    setIsDarkTheme(readTheme());
    const obs = new MutationObserver(() => setIsDarkTheme(readTheme()));
    obs.observe(root, { attributes: true, attributeFilter: ["class", "data-theme"] });
    return () => obs.disconnect();
  }, []);

  // 🔐 Auth readiness for Product Performance API
  const authReady = useAuthReady();

  // Product Summary (shared with Overview)
  const [summary, setSummary] = React.useState(null);
  const [summaryLoading, setSummaryLoading] = React.useState(false);
  const [summaryError, setSummaryError] = React.useState("");

  // Top Products Viewed API
  const [topProductsData, setTopProductsData] = React.useState(null);
  const [topProductsLoading, setTopProductsLoading] = React.useState(true);
  const [topProductsError, setTopProductsError] = React.useState("");
  const [topProductByMonth, setTopProductByMonth] = React.useState(null);

  // Lender Distribution API
  const [lenderApiRows, setLenderApiRows] = React.useState(null);
  const [lenderLoading, setLenderLoading] = React.useState(false);
  const [lenderLoadedOnce, setLenderLoadedOnce] = React.useState(false);
  const [lenderError, setLenderError] = React.useState("");

  // Time to Cash API
  const [ttcData, setTtcData] = React.useState(null);
  const [ttcLoading, setTtcLoading] = React.useState(false);
  const [ttcError, setTtcError] = React.useState("");
  const ifTtcRangesRef = React.useRef(null);

  // Performance Metrics API
  const [perfData, setPerfData] = React.useState(null);
  const [perfLoading, setPerfLoading] = React.useState(false);
  const [perfError, setPerfError] = React.useState("");

  // Logged-in lender identity (email), populated from window and backend
  const [currentLenderEmail, setCurrentLenderEmail] = React.useState(() =>
    readLenderEmailFromSession()
  );

  // Re-hydrate lender email after auth is ready (covers delayed storage writes after login redirect).
  React.useEffect(() => {
    if (!authReady) return;
    const resolvedEmail = readLenderEmailFromSession();
    if (resolvedEmail && resolvedEmail !== currentLenderEmail) {
      setCurrentLenderEmail(resolvedEmail);
    }
  }, [authReady, currentLenderEmail]);

  // Fetch Product Summary (Total, Active, Avg deal size, Conversion, Lender ranking)
  // Refetches when the product-type dropdown changes (all / btl / if).
  React.useEffect(() => {
    if (!authReady) return undefined;
    let cancelled = false;

    async function loadSummary() {
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (cancelled) return;

      setSummaryLoading(true);
      setSummaryError("");
      try {
        const data = await getProductSummaryByType(product);
        if (cancelled) return;
        setSummary(data || null);
      } catch (e) {
        console.error("Analytics: failed to load product summary:", e);
        if (cancelled) return;
        setSummaryError(e?.message || "Failed to load summary KPIs");
        setSummary(null);
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    }

    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [authReady, product]);

  // Fetch Top Products Viewed (optional shouldAbort avoids duplicate work under Strict Mode / fast remounts)
  const fetchTopProductsViewed = React.useCallback(async (rangeLabel, shouldAbort) => {
    try {
      setTopProductsLoading(true);
      setTopProductsError("");
      setTopProductsData(null);

      const timeframe = getTimeframeFromRange(rangeLabel);
      const year = new Date().getFullYear();

      const data = await getMonthlyTotalViews(timeframe, year);
      if (shouldAbort?.()) return;
      setTopProductsData(data);
    } catch (e) {
      if (shouldAbort?.()) return;
      setTopProductsError(e?.message || "Failed to load top products viewed");
      setTopProductsData(null);
    } finally {
      if (!shouldAbort?.()) setTopProductsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!authReady) return;
    let cancelled = false;
    fetchTopProductsViewed(rangeLine, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [rangeLine, fetchTopProductsViewed, authReady]);

  // Top product per month for the clicks tooltip. Year-scoped, so load once.
  React.useEffect(() => {
    if (!authReady) return undefined;
    let cancelled = false;
    getTopProductMonthlyByTimeframe({ year: new Date().getFullYear(), productType: "all" })
      .then((rows) => {
        if (!cancelled) setTopProductByMonth(rows);
      })
      .catch(() => {
        if (!cancelled) setTopProductByMonth(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authReady]);

  // Fetch Lender Distribution — reacts to rangePie changes
  const fetchLenderDistributionCb = React.useCallback(async (months, productType, shouldAbort) => {
    try {
      setLenderLoading(true);
      setLenderError("");
      const rows = await getLenderDistribution({ months, productType });
      if (shouldAbort?.()) return;
      setLenderApiRows(rows);
    } catch (e) {
      if (shouldAbort?.()) return;
      setLenderError(e?.message || "Failed to load lender distribution");
      setLenderApiRows(null);
    } finally {
      if (!shouldAbort?.()) {
        setLenderLoading(false);
        setLenderLoadedOnce(true);
      }
    }
  }, []);

  React.useEffect(() => {
    if (!authReady) return;
    let cancelled = false;
    const months = monthsFromRange(rangePie); // 1 / 3 / 6
    fetchLenderDistributionCb(months, product, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [rangePie, product, fetchLenderDistributionCb, authReady]);

  // Time to Cash — accepts months & purpose, uses ONLY real API data
  const fetchTimeToCash = React.useCallback(async (months, pKey, productType, shouldAbort) => {
    try {
      setTtcLoading(true);
      setTtcError("");
      setTtcData(null);

      if (productType === "if") {
        if (!ifTtcRangesRef.current) {
          ifTtcRangesRef.current = getIfTimeToCashPerStageRanges().catch((e) => {
            ifTtcRangesRef.current = null;
            console.warn("IF time-to-cash ranges failed, using per-range call:", e);
            return null;
          });
        }
        const ranges = await ifTtcRangesRef.current;
        const ifRes =
          pickIfTimeToCashRange(ranges, months) || (await getIfTimeToCashPerStage({ months }));
        if (shouldAbort?.()) return;
        setTtcData({ if: ifRes || {} });
        if (!hasAnyStageData(ifRes)) setTtcError("No time-to-cash data returned");
        return;
      }

      const res = await getTimeToCashPerStagePerLender({
        months,
        purpose: pKey,
      });

      if (shouldAbort?.()) return;

      const msg = normalizeTtcKeys(res || {});
      const hasAny = Object.values(msg || {}).some((block) =>
        hasAnyStageData(block)
      );

      if (!hasAny) {
        setTtcData(msg);
        setTtcError("No time-to-cash data returned");
        return;
      }

      setTtcData(msg);

      const candidates = [
        "purchase",
        "remortgage",
        "product transfer",
      ];

      const pick = candidates.find((pp) => hasAnyStageData(msg[pp]));
      if (shouldAbort?.()) return;
      if (pick) setPurpose(pick);
    } catch (e) {
      console.error("Time to Cash API failed:", e);
      if (shouldAbort?.()) return;
      setTtcError("Failed to load time-to-cash data");
      setTtcData(null);
    } finally {
      if (!shouldAbort?.()) setTtcLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!authReady) return;
    let cancelled = false;
    const months = monthsFromRange(rangeBar);
    const pKey = (purpose || "purchase").toLowerCase();
    fetchTimeToCash(months, pKey, product, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [rangeBar, purpose, product, fetchTimeToCash, authReady]);

  // Performance Metrics (shouldAbort skips applying results; clears in-flight retries on unmount)
  const fetchPerformanceMetrics = React.useCallback((months, productType, shouldAbort) => {
    let retryTimer = null;

    const attemptFetch = async (attempt) => {
      if (shouldAbort?.()) return;
      try {
        if (attempt === 1) {
          setPerfLoading(true);
          setPerfError("");
          setPerfData(null);
        }
        const metrics = await getAllPerformanceMetrics(months, productType);
        if (shouldAbort?.()) return;
        setPerfData(metrics);
        setPerfLoading(false);
      } catch (e) {
        if (shouldAbort?.()) return;
        const status = e?.status;

        if (status === 401 && attempt < 3) {
          const delay = 400 * attempt;
          console.warn(
            `Product Performance: auth not ready yet (status ${status}). Retrying in ${delay}ms...`
          );
          retryTimer = window.setTimeout(() => {
            retryTimer = null;
            attemptFetch(attempt + 1);
          }, delay);
          return;
        }

        console.error("Error loading performance metrics:", e);
        setPerfError(e?.message || "Failed to load performance metrics");
        setPerfData(null);
        setPerfLoading(false);
      }
    };

    attemptFetch(1);

    return () => {
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, []);

  React.useEffect(() => {
    if (!authReady) return;
    let cancelled = false;
    const months = monthsFromRange(rangePerf);
    const cleanup = fetchPerformanceMetrics(months, product, () => cancelled);
    return () => {
      cancelled = true;
      if (typeof cleanup === "function") cleanup();
    };
  }, [rangePerf, product, fetchPerformanceMetrics, authReady]);

  /* ---------------- Charts ---------------- */
  const pdata = React.useMemo(() => {
    return PRODUCT_DB?.[product] || EMPTY_DATASET;
  }, [product]);

  const clicksBarCfg = React.useMemo(() => {
    const { labels, values } = buildClicksChartForRange(topProductsData, rangeLine);
    return {
      labels,
      datasets: [
        {
          label: "Total clicks",
          data: values,
          backgroundColor: "rgba(56,189,248,0.75)",
          borderColor: "#0EA5E9",
          borderWidth: 1,
          borderRadius: 6,
          barThickness: 18,
        },
      ],
    };
  }, [topProductsData, rangeLine]);

  const chartTextColor = isDarkTheme ? "#F8FAFC" : "#334155";
  const chartGridColor = isDarkTheme ? "rgba(248,250,252,0.22)" : "rgba(0,0,0,0.06)";

  const chartCommon = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: chartTextColor },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        titleColor: "#F8FAFC",
        bodyColor: "#F8FAFC",
      },
    },
    interaction: { mode: "nearest", axis: "x", intersect: false },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: chartTextColor },
      },
      y: {
        beginAtZero: true,
        grid: { color: chartGridColor },
        ticks: { color: chartTextColor },
      },
    },
  };

  const clicksOptions = {
    ...chartCommon,
    plugins: {
      ...chartCommon.plugins,
      tooltip: {
        ...chartCommon.plugins.tooltip,
        callbacks: {
          afterLabel: (ctx) => {
            const top = topProductByMonth?.[MONTH_LABELS_12.indexOf(ctx.label)];
            if (!top?.name) return "";
            return top.views ? `Top: ${top.name} (${top.views})` : `Top: ${top.name}`;
          },
        },
      },
    },
  };

  const isIfProduct = product === "if";
  const lower = (s) => (s ? String(s).toLowerCase() : "");
  const purposeKey = [
    "purchase",
    "remortgage",
    "product transfer",
    "transfer",
    "btl",
  ].includes(lower(purpose))
    ? lower(purpose)
    : "purchase";

  const block = isIfProduct
    ? ttcData?.if || null
    : ttcData?.[purposeKey] ||
      (purposeKey === "transfer" ? ttcData?.["product transfer"] : null) ||
      ttcData?.btl ||
      null;

  const apiAvg = getStageBlock(block);
  const hasApiStages = hasAnyStageData(block);

  // ✅ Option A: normalize stage values (clamp negatives to 0) + track anomaly count
  const { ttcStageDays, ttcAnomalyCount } = React.useMemo(() => {
    if (!hasApiStages || !apiAvg) {
      return { ttcStageDays: null, ttcAnomalyCount: 0 };
    }

    const rawStages = [
      { stage: "Enquiry", days: apiAvg?.Enquiry ?? apiAvg?.enquiry ?? 0 },
      {
        stage: "Application",
        days: apiAvg?.Application ?? apiAvg?.application ?? 0,
      },
      { stage: "Approval", days: apiAvg?.Approval ?? apiAvg?.approval ?? 0 },
      {
        stage: isIfProduct ? "Audit" : "Valuation",
        days: isIfProduct
          ? apiAvg?.Audit ?? apiAvg?.audit ?? apiAvg?.Valuation ?? apiAvg?.valuation ?? 0
          : apiAvg?.Valuation ?? apiAvg?.valuation ?? 0,
      },
      { stage: "Legals", days: apiAvg?.Legals ?? apiAvg?.legals ?? 0 },
      {
        stage: "Completion",
        days: apiAvg?.Completion ?? apiAvg?.completion ?? 0,
      },
    ];

    const { data, anomalyCount } = normalizeTimeToCashStages(rawStages);
    return {
      ttcStageDays: data.map((s) => Number(s.days ?? 0)),
      ttcAnomalyCount: anomalyCount,
    };
  }, [
    hasApiStages,
    apiAvg,
    isIfProduct,
  ]);

  const ttcStageCounts = React.useMemo(() => {
    // IF sends `stages` as a list of names; counts (if provided) come in a separate object.
    const stages = Array.isArray(block?.stages)
      ? block?.stage_counts || block?.counts_per_stage || null
      : block?.stages;
    if (!stages || typeof stages !== "object") return null;
    return [
      Number(stages?.Enquiry ?? stages?.enquiry ?? 0),
      Number(stages?.Application ?? stages?.application ?? 0),
      Number(stages?.Approval ?? stages?.approval ?? 0),
      Number(
        isIfProduct
          ? stages?.Audit ?? stages?.audit ?? stages?.Valuation ?? stages?.valuation ?? 0
          : stages?.Valuation ?? stages?.valuation ?? 0
      ),
      Number(stages?.Legals ?? stages?.legals ?? 0),
      Number(stages?.Completion ?? stages?.completion ?? 0),
    ];
  }, [block, isIfProduct]);

  const effectiveTtcMode = ttcMode;
  const ttcDataset = effectiveTtcMode === "avgDays" ? ttcStageDays : ttcStageCounts;

  const ttcBarCfg = {
    labels: [
      "Enquiry",
      "Application",
      "Approval",
      isIfProduct ? "Audit" : "Valuation",
      "Legals",
      "Completion",
    ],
    datasets: [
      {
        label: effectiveTtcMode === "avgDays" ? "Avg days" : "Stage count",
        data: Array.isArray(ttcDataset) ? ttcDataset : [],
        backgroundColor: [
          "#22C55E",
          "#0EA5E9",
          "#38BDF8",
          "#F59E0B",
          "#EF4444",
          "#8B5CF6",
        ],
        borderRadius: 8,
      },
    ],
  };

  // Product Performance - Using API data with correct field extraction
  const perfValues = React.useMemo(() => {
    if (!perfData) return [0, 0, 0, 0];

    let approvalRate = 0;
    const rawApprovalRate =
      perfData.approvalRate?.approval_rate ?? perfData.approvalRate?.overall_approval_rate;
    if (rawApprovalRate) {
      const rateStr = String(rawApprovalRate).replace("%", "").trim();
      approvalRate = parseFloat(rateStr) || 0;
    }

    let topPerformer = 0;
    if (perfData.topPerformer?.top_products?.[0]?.percentage) {
      const perfStr = String(perfData.topPerformer.top_products[0].percentage)
        .replace("%", "")
        .trim();
      topPerformer = parseFloat(perfStr) || 0;
    }

    let avgLoan = 0;
    if (perfData.avgLoan?.average_loan_amount !== undefined) {
      avgLoan = Number(perfData.avgLoan.average_loan_amount) || 0;
    } else if (
      Array.isArray(perfData.avgLoan) &&
      perfData.avgLoan[0]?.average_loan_amount !== undefined
    ) {
      avgLoan = Number(perfData.avgLoan[0].average_loan_amount) || 0;
    }
    const avgLoanPercentage = avgLoan
      ? Math.min(100, Math.round((avgLoan / 750000) * 100))
      : 0;

    let satisfaction = 0;
    if (perfData.satisfaction?.overall_satisfaction !== undefined) {
      satisfaction = Number(perfData.satisfaction.overall_satisfaction) || 0;
    }

    const clampPct = (v) => Math.min(100, Math.max(0, Number(v) || 0));
    return [approvalRate, topPerformer, avgLoanPercentage, satisfaction].map(clampPct);
  }, [perfData]);

  const perfCfg = React.useMemo(
    () => ({
      labels: [
        "Approval Rate",
        "Top Performer",
        "Avg Loan Amount",
        "Borrower Satisfaction",
      ],
      datasets: [
        {
          label: "Performance",
          data: perfValues,
          borderSkipped: false,
          borderRadius: 14,
          barThickness: 18,
          backgroundColor: ["#0EA5E9", "#22C55E", "#F59E0B", "#EF4444"],
        },
      ],
    }),
    [perfValues]
  );

  const perfOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        titleColor: "#F8FAFC",
        bodyColor: "#F8FAFC",
        callbacks: {
          label: (ctx) => {
            const ds = ctx?.dataset?.data || [];
            const val = Number(ds[ctx.dataIndex] ?? 0);
            return `${val}%`;
          },
        },
      },
    },
    scales: {
      x: {
        max: 100,
        beginAtZero: true,
        grid: { color: chartGridColor },
        ticks: { callback: (v) => `${v}%`, color: chartTextColor },
      },
      y: { grid: { display: false }, ticks: { color: chartTextColor } },
    },
  };

  // Lender Distribution – use ONLY live backend data (no fallbacks)
  const lendersForPie = React.useMemo(
    () => (Array.isArray(lenderApiRows) ? lenderApiRows : []),
    [lenderApiRows]
  );
  const lenderAgg = React.useMemo(() => {
    const rows = Array.isArray(lendersForPie) ? lendersForPie : [];
    if (!rows.length) {
      return {
        labelsRaw: [],
        labels: [],
        values: [],
        detailsByLabel: {},
        myPieLabel: null,
      };
    }
    const me = currentLenderEmail;
    let myPieLabel = null;
    const ranked = rows
      .map((r) => ({
        rawLabel: String(r?.lender || "").trim() || "Unassigned",
        value: Number(r?.share ?? r?.top_performer_score ?? 0),
        details: {
          approvalRate: Number(r?.approvalRate ?? r?.approval_rate ?? 0),
          topPerformer: Number(r?.topPerformer ?? r?.top_performer_score ?? 0),
          avgLoan: Number(r?.avgLoan ?? r?.avg_loan_amount ?? 0),
          satisfaction: Number(r?.satisfaction ?? r?.borrower_satisfaction ?? 0),
        },
      }))
      .sort((a, b) => b.value - a.value || a.rawLabel.localeCompare(b.rawLabel));

    const isOthersLabel = (label) => {
      const l = String(label || "").toLowerCase();
      return l === "others" || l === "unassigned";
    };

    const othersRow = ranked.find((r) => isOthersLabel(r.rawLabel)) || null;
    const topLenders = ranked.filter((r) => !isOthersLabel(r.rawLabel)).slice(0, 5);
    const selected = [...topLenders];

    // Include "Others" only when backend actually provides it.
    if (othersRow) {
      selected.push(othersRow);
    }

    const finalSlices = selected.map((entry, idx) => {
      const isOthers = isOthersLabel(entry.rawLabel);
      const isCurrent = !isOthers && me && isSameLender(entry.rawLabel, me);
      const lenderRankLabel = `Lender ${idx + 1}`;

      let label;
      if (isOthers) {
        label = "Others";
      } else if (isCurrent) {
        label = `${toLenderDisplayName(entry.rawLabel)} (${lenderRankLabel})`;
        myPieLabel = label;
      } else {
        label = lenderRankLabel;
      }

      return {
        rawLabel: entry.rawLabel,
        label,
        value: entry.value > 0 ? entry.value : 1, // keep visible if API value is 0
        details: entry.details,
      };
    });

    const newDetailsByLabel = {};
    finalSlices.forEach((s) => {
      newDetailsByLabel[s.label] = s.details;
    });

    return {
      labelsRaw: finalSlices.map((s) => s.rawLabel),
      labels: finalSlices.map((s) => s.label),
      values: finalSlices.map((s) => s.value),
      detailsByLabel: newDetailsByLabel,
      myPieLabel,
    };
  }, [lendersForPie, currentLenderEmail]);
  const isCurrentLenderInTop = React.useMemo(() => {
    return !!lenderAgg?.myPieLabel;
  }, [lenderAgg]);

  const myLabelForPie = lenderAgg?.myPieLabel || null;

  // Animate current lender slice: pop out briefly, then return.
  const [highlightMine, setHighlightMine] = React.useState(false);
  const popTimerRef = React.useRef(null);
  const resetTimerRef = React.useRef(null);

  React.useEffect(() => {
    if (!isCurrentLenderInTop || !myLabelForPie) return;

    // Reset any previous timers when pie data updates.
    if (popTimerRef.current) clearTimeout(popTimerRef.current);
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);

    // Start from normal, then pop smoothly.
    setHighlightMine(false);
    popTimerRef.current = setTimeout(() => setHighlightMine(true), 120);
    resetTimerRef.current = setTimeout(() => setHighlightMine(false), 1800);

    return () => {
      if (popTimerRef.current) clearTimeout(popTimerRef.current);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, [isCurrentLenderInTop, myLabelForPie, lenderAgg.labels.join("|"), lenderAgg.values.join("|")]);

  const lenderPieCfg = {
    labels: lenderAgg.labels,
    datasets: [
      {
        data: lenderAgg.values,
        backgroundColor: pieColors.slice(0, lenderAgg.labels.length),
        borderWidth: 1,
        offset: (ctx) => {
          const label = ctx.chart?.data?.labels?.[ctx.dataIndex];
          if (!label || !myLabelForPie) return 0;
          // Pop out briefly, then return to normal.
          return highlightMine && label === myLabelForPie ? 24 : 0;
        },
      },
    ],
  };
  const lenderPieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 650,
      easing: "easeOutCubic",
    },
    plugins: {
      legend: {
        position: "top",
        labels: { color: chartTextColor },
      },
      tooltip: {
        titleColor: "#F8FAFC",
        bodyColor: "#F8FAFC",
        callbacks: {
          label: (ctx) => {
            const ds = ctx.dataset?.data || [];
            const val = Number(ds[ctx.dataIndex] ?? 0);
            const total = ds.reduce((a, b) => a + Number(b || 0), 0);
            const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";
            const score =
              Number.isFinite(val) && !Number.isInteger(val)
                ? val.toFixed(2)
                : String(Math.round(val));
            return `${ctx.label}: score ${score} (${pct}% of chart)`;
          },
          afterLabel: (ctx) => {
            const d = lenderAgg.detailsByLabel[ctx.label];
            if (!d) return [];
            const ar = Number(d.approvalRate ?? 0);
            const tp = Number(d.topPerformer ?? 0);
            const sat = Number(d.satisfaction ?? 0);
            return [
              `Approval rate: ${ar}%`,
              `Top performer score: ${Number.isFinite(tp) ? tp.toFixed(2) : tp}`,
              `Avg. loan amount: ${formatGBP0(d.avgLoan)}`,
              `Borrower satisfaction: ${sat}%`,
            ];
          },
        },
      },
    },
  };

  const myRank = React.useMemo(
    () => getMyLenderRank(lendersForPie || [], currentLenderEmail),
    [lendersForPie, currentLenderEmail]
  );
  const myRankDisplay = myRank ? `#${myRank}` : "—";

  const avgDealSize = React.useMemo(() => {
    if (!perfData?.avgLoan) return 0;

    if (Array.isArray(perfData.avgLoan) && perfData.avgLoan.length > 0) {
      const item = perfData.avgLoan[0];
      return Number(item?.average_loan_amount || 0);
    }

    if (typeof perfData.avgLoan === "object") {
      return Number(perfData.avgLoan.average_loan_amount || 0);
    }

    return 0;
  }, [perfData]);

  // ---- KPIs from product summary (shared with Overview) with fallback ----
  const analyticsTotalProducts =
    summary?.total_products ?? (PRODUCT_DB?.[product]?.kpi?.total ?? 0);

  const analyticsActiveProducts =
    summary?.active_products ?? (PRODUCT_DB?.[product]?.kpi?.active ?? 0);

  const analyticsAvgDealSize = React.useMemo(() => {
    if (summary?.average_deal_size?.value != null) {
      return formatGBP0(summary.average_deal_size.value);
    }
    return formatGBP0(avgDealSize);
  }, [summary, avgDealSize]);

  const analyticsConversionRate =
    summary?.conversion_rate?.value ??
    `${(PRODUCT_DB?.[product]?.kpi?.conversion ?? 0).toFixed(1)} %`;

  const lenderRankDisplay =
    summary?.lender_ranking?.current_user?.rank || myRankDisplay;

  // Smooth KPI loading flag
  const kpiLoading = summaryLoading && !summary;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HERO */}
      <div className="px-4 md:px-6 pt-6 pb-4">
        <Hero
          title="Analytics Dashboard"
          subtitle="Monitor applications, time-to-cash, lender distribution, and product performance."
          right={
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/90">Product</span>
              <select
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                className="bg-white text-slate-800 rounded-lg px-3 py-1.5 border border-white/30 shadow-sm focus:outline-none focus:ring-2 focus:ring-white/40 text-sm"
              >
                {PRODUCT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          }
        />

        {summaryLoading && (
          <p className="mt-2 text-xs text-slate-600 flex items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            Loading summary KPIs…
          </p>
        )}

        {summaryError && (
          <p className="mt-2 text-xs bg-red-100 border border-red-300/60 inline-flex px-2 py-1 rounded text-red-700">
            {summaryError}
          </p>
        )}
      </div>

      {/* KPIs (smooth loading via skeleton) */}
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        <AKpi
          title="Total Products"
          value={String(analyticsTotalProducts)}
          icon={Upload}
          loading={kpiLoading}
        />
        <AKpi
          title="Active Products"
          value={String(analyticsActiveProducts)}
          icon={PieChart}
          loading={kpiLoading}
        />
        <AKpi
          title="Average Deal Size"
          value={analyticsAvgDealSize}
          icon={TrendingUp}
          loading={kpiLoading}
        />
        <AKpi
          title="Conversion Rate"
          value={analyticsConversionRate}
          icon={LineChart}
          loading={kpiLoading}
        />
        <AKpi
          title="Lender Ranking"
          value={lenderRankDisplay}
          icon={Trophy}
          loading={kpiLoading}
        />
      </div>

      {/* Charts */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid gap-6 lg:grid-cols-2 mt-6 pb-8">
        {/* Total clicks */}
        <ACard className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-gray-700">
                Total clicks
              </div>
              {topProductsLoading ? (
                <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Loading...
                </span>
              ) : topProductsError ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Using fallback
                </span>
              ) : topProductsData ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Live
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <RangeDropdown value={rangeLine} onChange={setRangeLine} />
              {topProductsError && (
                <button
                  onClick={() => fetchTopProductsViewed(rangeLine)}
                  className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-1"
                  disabled={topProductsLoading}
                  title="Retry API call"
                >
                  <RefreshCw
                    className={`w-3 h-3 ${topProductsLoading ? "animate-spin" : ""
                      }`}
                  />
                  Retry
                </button>
              )}
            </div>
          </div>
          <div className="h-44">
            {topProductsLoading || !Array.isArray(topProductsData) ? (
              <div className="h-full grid place-items-center">
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading total clicks...
                </div>
              </div>
            ) : (
              <CBar data={clicksBarCfg} options={clicksOptions} />
            )}
          </div>
        </ACard>

        {/* Time to Cash */}
        <ACard className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-gray-700">Time to Cash</div>
             
            </div>
            <div className="flex items-center gap-2">
              <select
                value={ttcMode}
                onChange={(e) => setTtcMode(e.target.value)}
                className="text-xs border rounded px-2 py-1 bg-white"
              >
                <option value="count">Stage count</option>
                <option value="avgDays">Avg days</option>
              </select>
              {!isIfProduct && <PurposeDropdown value={purpose} onChange={setPurpose} />}
              <RangeDropdown value={rangeBar} onChange={setRangeBar} />
            </div>
          </div>
          <div className="h-44 relative">
            {ttcLoading ? (
              <div className="h-full grid place-items-center">
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading time-to-cash...
                </div>
              </div>
            ) : ttcError ? (
              <div className="h-full grid place-items-center">
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  {ttcError}
                </div>
              </div>
            ) : hasApiStages && Array.isArray(ttcDataset) ? (
              <div className="w-full h-full">
                <CBar data={ttcBarCfg} options={chartCommon} />
              </div>
            ) : (
              <div className="h-full grid place-items-center">
                <div className="text-sm text-slate-500">
                  {hasApiStages && effectiveTtcMode === "count"
                    ? "No stage counts returned — switch to Avg days"
                    : "No time-to-cash data available"}
                </div>
              </div>
            )}
          </div>
        </ACard>

        {/* Product Performance */}
        <ACard className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-gray-700">
                Product Performance
              </div>
              {perfLoading || !authReady ? (
                <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  {authReady ? "Loading..." : "Waiting for auth..."}
                </span>
              ) : perfError ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Using fallback
                </span>
              ) : perfData ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Live
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <RangeDropdown value={rangePerf} onChange={setRangePerf} />
              {perfError && (
                <button
                  onClick={() => fetchPerformanceMetrics(monthsFromRange(rangePerf), product)}
                  className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-1"
                  disabled={perfLoading || !authReady}
                  title="Retry API call"
                >
                  <RefreshCw
                    className={`w-3 h-3 ${perfLoading ? "animate-spin" : ""}`}
                  />
                  Retry
                </button>
              )}
            </div>
          </div>
          <div className="h-56">
            {perfLoading || !authReady ? (
              <div className="h-full grid place-items-center">
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {authReady
                    ? "Loading performance data..."
                    : "Waiting for authentication..."}
                </div>
              </div>
            ) : (
              <CBar
                key={`perf-chart-${perfValues.join("-")}`}
                data={perfCfg}
                options={perfOptions}
              />
            )}
          </div>
        </ACard>

        {/* Lender Distribution */}
        <ACard className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-gray-700">
                Lender Distribution
              </div>
              {lenderLoading ? (
                <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Loading...
                </span>
              ) : lenderError ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Using fallback
                </span>
              ) : lenderApiRows ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Live
                </span>
              ) : null}
            </div>
            <RangeDropdown value={rangePie} onChange={setRangePie} />
          </div>
          <div className="h-56 grid place-items-center">
            {lenderLoading || !lenderLoadedOnce ? (
              <div className="text-sm text-slate-500 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading lender distribution...
              </div>
            ) : lenderAgg.labels.length ? (
              <div className="w-full h-full">
                <CPie data={lenderPieCfg} options={lenderPieOptions} />
              </div>
            ) : (
              <div className="text-sm text-slate-500">No data available</div>
            )}
          </div>
        </ACard>
      </div>
    </div>
  );
}

